import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { Box, Link, Button, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import {
  DataGrid,
  GridActionsCellItem,
  type GridRowParams,
  type GridFilterModel,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridColDef,
  type GridActionsColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router';
import type { GridRowId } from '@mui/x-data-grid';

import { useUser } from '@/providers/UserContext';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import { useApi } from '@/providers/ApiContext';
import {
  getManifests,
  getPendingManifests,
  approveManifest,
  rejectManifest,
} from '@/actions/manifest';
import type { Manifest, EntityId } from '@/types';

interface ManifestGridToolbarProps {
  onBulkApprove: () => void;
  onBulkReject: () => void;
  selectedRows: GridRowId[];
  bulkActionLoading: boolean;
  pending_only?: boolean;
}

function ManifestGridToolbar({
  onBulkApprove,
  onBulkReject,
  selectedRows,
  bulkActionLoading,
  pending_only,
}: ManifestGridToolbarProps): React.JSX.Element {
  return (
    <GridToolbarContainer
      sx={{
        '& .MuiButtonBase-root': {
          color: 'black',
        },
      }}
    >
      {pending_only && selectedRows?.length > 0 && (
        <>
          <Button
            sx={{ padding: '3px 9px' }}
            variant='outlined'
            color='success'
            onClick={onBulkApprove}
            disabled={bulkActionLoading}
            startIcon={<CheckIcon />}
          >
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='button' display='block' gutterBottom>
                Approve All ({selectedRows.length})
              </Typography>
            </Stack>
          </Button>
          <Button
            sx={{ padding: '3px 9px' }}
            variant='outlined'
            color='error'
            onClick={onBulkReject}
            disabled={bulkActionLoading}
            startIcon={<CloseIcon />}
          >
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='button' display='block' gutterBottom>
                Reject All ({selectedRows.length})
              </Typography>
            </Stack>
          </Button>
        </>
      )}
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter
        debounceMs={500}
        placeholder='Search manifests...'
      />
    </GridToolbarContainer>
  );
}

interface AdminManifestListProps {
  pending_only?: boolean;
  document_type_id?: EntityId;
  setSelected: (ids: GridRowId[], names?: string[]) => void;
}

function AdminManifestList({
  pending_only,
  document_type_id,
  setSelected,
}: AdminManifestListProps): React.JSX.Element {
  const user = useUser();
  const api = useApi();
  const [manifests, setManifests] = useState<Manifest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>(),
    });
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [cursors, setCursors] = useState<Record<number, string | null>>({});
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [bulkActionLoading, setBulkActionLoading] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [debouncedSearchFilter, setDebouncedSearchFilter] =
    useState<string>('');
  const [columnFilters, setColumnFilters] = useState<Record<string, unknown>>(
    {},
  );
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState<
    Record<string, unknown>
  >({});
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterValues: [],
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const columnFilterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce search filter changes - only trigger fetch, don't reset state
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchFilter(searchFilter);
    }, 500); // Longer delay to prevent excessive API calls

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchFilter]);

  // Debounce column filter changes - only trigger fetch, don't reset state
  useEffect(() => {
    if (columnFilterTimeoutRef.current) {
      clearTimeout(columnFilterTimeoutRef.current);
    }

    columnFilterTimeoutRef.current = setTimeout(() => {
      setDebouncedColumnFilters(columnFilters);
    }, 200); // Shorter delay for dropdown selections

    return () => {
      if (columnFilterTimeoutRef.current) {
        clearTimeout(columnFilterTimeoutRef.current);
      }
    };
  }, [columnFilters]);

  const fetchManifests = useCallback(
    async (
      currentPage: number,
      startKey: string | null = null,
    ): Promise<void> => {
      if (!api) return;
      try {
        setLoading(true);
        let response;
        if (pending_only) {
          response = await getPendingManifests(
            api,
            pageSize,
            startKey || undefined,
            debouncedSearchFilter || undefined,
          );
        } else if (document_type_id) {
          response = await getManifests(
            api,
            document_type_id,
            pageSize,
            startKey || undefined,
            debouncedSearchFilter || undefined,
          );
        } else {
          return;
        }

        setManifests(response.results);
        setHasNextPage(response.last_key !== null);
        if (response.last_key) {
          setCursors((prev) => ({
            ...prev,
            [currentPage + 1]: response.last_key,
          }));
        }
      } catch (error) {
        console.error('Error fetching manifests:', error);
        const err = error as Error;
        setApiError(
          err.message ||
            'Unable to fetch manifests. This could be due to network issues or server problems.',
        );
      } finally {
        setLoading(false);
      }
    },
    [api, pending_only, document_type_id, pageSize, debouncedSearchFilter],
  );

  useEffect(() => {
    if (user?.entity_id) {
      setCurrentPage(0);
      setCursors({});
      fetchManifests(0);
    }
  }, [user?.entity_id, document_type_id, fetchManifests]);

  // Reset pagination when debounced search filter or column filters change
  useEffect(() => {
    if (user?.entity_id) {
      setCurrentPage(0);
      setCursors({});
      // Don't reset manifests to null - just fetch new data
      fetchManifests(0);
    }
  }, [
    debouncedSearchFilter,
    debouncedColumnFilters,
    user?.entity_id,
    fetchManifests,
  ]);

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel): void => {
      console.log('Filter model changed:', model);

      // Update the filter model state to maintain it
      setFilterModel(model);

      // Handle QuickFilter - only update if it's actually different
      const newSearchFilter = model.quickFilterValues?.join(' ') || '';
      if (newSearchFilter !== searchFilter) {
        console.log(
          'Search filter changed from:',
          searchFilter,
          'to:',
          newSearchFilter,
        );
        setSearchFilter(newSearchFilter);
      }

      // Handle column filters
      const newColumnFilters: Record<string, unknown> = {};
      if (model.items) {
        model.items.forEach((item) => {
          if (
            item.field &&
            item.value !== undefined &&
            item.value !== null &&
            item.value !== ''
          ) {
            newColumnFilters[item.field] = item.value;
          }
        });
      }

      console.log('New column filters:', newColumnFilters);

      // Only update if column filters have changed
      const columnFiltersChanged =
        JSON.stringify(newColumnFilters) !== JSON.stringify(columnFilters);
      if (columnFiltersChanged) {
        setColumnFilters(newColumnFilters);
      }
    },
    [searchFilter, columnFilters],
  );

  const handleRowSelection = useCallback(
    (value: GridRowSelectionModel): void => {
      setRowSelectionModel(value);
      const ids = Array.from(value.ids);
      const names = ids.map((id) =>
        manifests.find((m) => m.entity_id === id)?.name ?? String(id),
      );
      setSelected(ids, names);
    },
    [setSelected, manifests],
  );

  const handlePaginationModelChange = (model: GridPaginationModel): void => {
    if (model.page > currentPage && hasNextPage) {
      setCurrentPage(model.page);
      const nextCursor = cursors[model.page];
      fetchManifests(model.page, nextCursor || null);
    } else if (model.page < currentPage) {
      setCurrentPage(model.page);
      if (model.page === 0) {
        fetchManifests(0);
      } else {
        const prevCursor = cursors[model.page];
        fetchManifests(model.page, prevCursor || null);
      }
    }

    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
      setCurrentPage(0);
      setCursors({});
      fetchManifests(0);
    }
  };

  const renderName = useCallback(
    (params: GridRenderCellParams<Manifest>): React.JSX.Element => {
      return (
        <Link
          component={RouterLink}
          to={`/admin/manifests/${params.row.entity_id}`}
        >
          {params.row.name}
        </Link>
      );
    },
    [],
  );

  const convertDate = useCallback((value: string): Date | null => {
    return value ? new Date(value.replace(' ', 'T') + 'Z') : null;
  }, []);

  const handleApprove = useCallback(
    async (manifestId: EntityId): Promise<void> => {
      if (!api) return;
      try {
        setActionLoading((prev) => ({ ...prev, [manifestId]: true }));
        await approveManifest(api, manifestId);
        // Refresh the current page
        const currentCursor =
          currentPage === 0 ? null : cursors[currentPage] || null;
        fetchManifests(currentPage, currentCursor);
      } catch (error) {
        console.error('Error approving manifest:', error);
        const err = error as Error;
        setApiError(
          err.message ||
            'Unable to approve the manifest. This could be due to network issues, server problems, or insufficient permissions.',
        );
      } finally {
        setActionLoading((prev) => ({ ...prev, [manifestId]: false }));
      }
    },
    [api, currentPage, cursors, fetchManifests],
  );

  const handleReject = useCallback(
    async (manifestId: EntityId): Promise<void> => {
      if (!api) return;
      try {
        setActionLoading((prev) => ({ ...prev, [manifestId]: true }));
        await rejectManifest(api, manifestId);
        // Refresh the current page
        const currentCursor =
          currentPage === 0 ? null : cursors[currentPage] || null;
        fetchManifests(currentPage, currentCursor);
      } catch (error) {
        console.error('Error rejecting manifest:', error);
        const err = error as Error;
        setApiError(
          err.message ||
            'Unable to reject the manifest. This could be due to network issues, server problems, or insufficient permissions.',
        );
      } finally {
        setActionLoading((prev) => ({ ...prev, [manifestId]: false }));
      }
    },
    [api, currentPage, cursors, fetchManifests],
  );

  const handleBulkApprove = useCallback(async (): Promise<void> => {
    if (rowSelectionModel.ids.size === 0 || !api) return;

    try {
      setBulkActionLoading(true);
      // Process all selected manifests in parallel
      await Promise.all(
        Array.from(rowSelectionModel.ids).map((manifestId) =>
          approveManifest(api, manifestId as EntityId),
        ),
      );
      // Refresh the current page
      const currentCursor =
        currentPage === 0 ? null : cursors[currentPage] || null;
      fetchManifests(currentPage, currentCursor);
      // Clear selection
      setRowSelectionModel({ type: 'include', ids: new Set<GridRowId>() });
      setSelected([]);
    } catch (error) {
      console.error('Error bulk approving manifests:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to approve some manifests. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setBulkActionLoading(false);
    }
  }, [
    rowSelectionModel,
    api,
    currentPage,
    cursors,
    fetchManifests,
    setSelected,
  ]);

  const handleBulkReject = useCallback(async (): Promise<void> => {
    if (rowSelectionModel.ids.size === 0 || !api) return;

    try {
      setBulkActionLoading(true);
      // Process all selected manifests in parallel
      await Promise.all(
        Array.from(rowSelectionModel.ids).map((manifestId) =>
          rejectManifest(api, manifestId as EntityId),
        ),
      );
      // Refresh the current page
      const currentCursor =
        currentPage === 0 ? null : cursors[currentPage] || null;
      fetchManifests(currentPage, currentCursor);
      // Clear selection
      setRowSelectionModel({ type: 'include', ids: new Set<GridRowId>() });
      setSelected([]);
    } catch (error) {
      console.error('Error bulk rejecting manifests:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to reject some manifests. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setBulkActionLoading(false);
    }
  }, [
    rowSelectionModel,
    api,
    currentPage,
    cursors,
    fetchManifests,
    setSelected,
  ]);

  const getActions = useCallback(
    (params: GridRowParams<Manifest>) => {
      const manifestId = params.row.entity_id;
      const isLoading = actionLoading[manifestId] || false;

      return [
        <GridActionsCellItem
          key='approve'
          icon={<CheckIcon />}
          label='Approve'
          onClick={() => handleApprove(manifestId)}
          showInMenu
          disabled={isLoading}
        />,
        <GridActionsCellItem
          key='reject'
          icon={<CloseIcon />}
          label='Reject'
          onClick={() => handleReject(manifestId)}
          showInMenu
          disabled={isLoading}
        />,
      ];
    },
    [actionLoading, handleApprove, handleReject],
  );

  const columns = useMemo((): GridColDef<Manifest>[] => {
    const baseColumns: GridColDef<Manifest>[] = [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        type: 'string' as const,
        renderCell: renderName,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string' as const,
        flex: 0.5,
      },
      {
        field: 'last_modified_by',
        headerName: 'Last Modified By',
        type: 'string' as const,
        flex: 0.75,
      },
      {
        field: 'created_at',
        headerName: 'Started',
        type: 'dateTime' as const,
        flex: 0.75,
        valueGetter: (_value: unknown, row: Manifest) =>
          convertDate(row.created_at),
      },
      {
        field: 'updated_at',
        headerName: 'Last Updated',
        type: 'dateTime' as const,
        flex: 0.75,
        valueGetter: (_value: unknown, row: Manifest) =>
          convertDate(row.updated_at),
      },
    ];

    // Add Actions column when pending_only is true
    if (pending_only) {
      baseColumns.push({
        field: 'actions',
        headerName: 'Actions',
        type: 'actions' as const,
        flex: 0.5,
        getActions: getActions,
      } as GridActionsColDef<Manifest>);
    } else {
      baseColumns.push({
        field: 'approved_by',
        headerName: 'Approved By',
        type: 'string' as const,
        flex: 0.75,
      });
    }

    return baseColumns;
  }, [renderName, convertDate, pending_only, getActions]);

  if (loading && !manifests.length) {
    return <LoadingMask />;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          height: '100%',
        }}
      >
        <Box
          sx={{
            height: 'calc(100vh - 200px)',
            width: '100%',
          }}
        >
          <DataGrid
            checkboxSelection
            columnHeaderHeight={35}
            sx={(theme) => ({
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.grey[100],
              },
            })}
            getRowId={(row) => row.entity_id}
            columns={columns}
            rows={manifests}
            loading={loading}
            paginationMode='server'
            filterMode='server'
            filterModel={filterModel}
            rowCount={
              hasNextPage ? -1 : currentPage * pageSize + manifests.length
            }
            pageSizeOptions={[50, 100]}
            paginationModel={{
              pageSize,
              page: currentPage,
            }}
            onPaginationModelChange={handlePaginationModelChange}
            onFilterModelChange={handleFilterModelChange}
            onRowSelectionModelChange={handleRowSelection}
            rowSelectionModel={rowSelectionModel}
            showToolbar
            slots={{
              toolbar: () => (
                <ManifestGridToolbar
                  onBulkApprove={handleBulkApprove}
                  onBulkReject={handleBulkReject}
                  selectedRows={Array.from(rowSelectionModel.ids)}
                  bulkActionLoading={bulkActionLoading}
                  pending_only={pending_only}
                />
              ),
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  // Hide columns status and traderName, the other columns will remain visible
                  approved_by: false,
                  last_modified_by: false,
                },
              },
            }}
          />
        </Box>
      </Box>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Box>
  );
}

export default AdminManifestList;
